import React, { useEffect, useState } from 'react';
import { MapPin, Clock, Phone } from 'lucide-react';
import Layout from '../Layout';
import TitleSection from '../TitleSection';
import { getData } from '../../helpers/ApiSettings';

// Import dynamique du composant de carte
const SimpleMapComponent = React.lazy(() => import('../SimpleMapComponent'));

const RelayPoints = () => {
    const [selectedArea, setSelectedArea] = useState('all');
    const [relayPoints, setRelayPoints] = useState([]);
    const [mapVisible, setMapVisible] = useState(true);

    const areas = ['all', 'Grande-Terre', 'Basse-Terre'];

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const response = await getData('/relay-points');
            // Ajouter des coordonnées fictives si elles ne sont pas dans l'API
            const pointsWithCoordinates = response.data.data.map(point => ({
                ...point,
                latitude: point.latitude || getDefaultLatitude(point.area),
                longitude: point.longitude || getDefaultLongitude(point.area)
            }));
            setRelayPoints(pointsWithCoordinates);
        } catch (e) {
            console.error("Error fetching relay points: ", e);
        }
    };

    // Fonctions pour obtenir des coordonnées par défaut selon la zone
    const getDefaultLatitude = (area) => {
        if (area === 'Grande-Terre') return 16.265;
        if (area === 'Basse-Terre') return 16.014;
        return 16.2419; // Défaut pour la Guadeloupe
    };

    const getDefaultLongitude = (area) => {
        if (area === 'Grande-Terre') return -61.3;
        if (area === 'Basse-Terre') return -61.7;
        return -61.5319; // Défaut pour la Guadeloupe
    };

    // Filtrer les points selon la zone sélectionnée
    const filteredPoints = selectedArea === 'all'
        ? relayPoints
        : relayPoints.filter(point => point.area === selectedArea);

    return (
        <Layout>
            <TitleSection title='Nos Points Relais' />
            <div className="container pb-5">
                <div className="row mb-5">
                    <div className="col-12 text-center">
                        <h1 className="display-5 mb-3">Nos Points Relais</h1>
                        <p className="text-muted mb-4">
                            Découvrez nos points relais partenaires en Guadeloupe pour récupérer votre matériel de puériculture
                        </p>

                        {/* Filtres par zone et boutons de vue */}
                        <div className="d-flex justify-content-center flex-wrap mb-4">
                            <div className="btn-group me-3">
                                {areas.map(area => (
                                    <button
                                        key={area}
                                        onClick={() => setSelectedArea(area)}
                                        className={`btn ${selectedArea === area
                                            ? 'btn-primary'
                                            : 'btn-outline-primary'
                                            }`}
                                    >
                                        {area === 'all' ? 'Tous les secteurs' : area}
                                    </button>
                                ))}
                            </div>
                            
                            <div className="btn-group">
                                <button 
                                    className={`btn ${mapVisible ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setMapVisible(true)}
                                >
                                    Carte
                                </button>
                                <button 
                                    className={`btn ${!mapVisible ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setMapVisible(false)}
                                >
                                    Liste
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Vue carte */}
                {mapVisible && (
                    <div className="row mb-5">
                        <div className="col-12">
                            <div className="card shadow-sm">
                                <div className="card-body p-4">
                                    <React.Suspense fallback={
                                        <div className="d-flex justify-content-center align-items-center" style={{ height: '500px' }}>
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Chargement de la carte...</span>
                                            </div>
                                        </div>
                                    }>
                                        <SimpleMapComponent relayPoints={filteredPoints} />
                                    </React.Suspense>
                                </div>
                            </div>
                            <p className="text-center text-muted mt-3">
                                <small>Cliquez sur un marqueur pour voir les détails du point relais</small>
                            </p>
                        </div>
                    </div>
                )}

                {/* Vue liste */}
                <div className={`row g-4 ${mapVisible ? 'd-none d-md-flex' : ''}`}>
                    {filteredPoints.map(point => (
                        <div key={point.id} className="col-12 col-md-6 col-lg-4">
                            <div className="card h-100 shadow-sm">
                                <div className="card-header bg-light">
                                    <h3 className="h5 mb-1">{point.name}</h3>
                                    <small className="text-muted">{point.area}</small>
                                </div>

                                <div className="card-body">
                                    <div className="d-flex mb-3">
                                        <MapPin className="flex-shrink-0 me-2 text-primary" />
                                        <p className="mb-0">
                                            {point.address}<br />
                                            {point.postal_code} {point.city}
                                        </p>
                                    </div>

                                    <div className="d-flex mb-3">
                                        <Phone className="flex-shrink-0 me-2 text-primary" />
                                        <p className="mb-0">{point.phone}</p>
                                    </div>

                                    <div className="d-flex mb-3">
                                        <Clock className="flex-shrink-0 me-2 text-primary" />
                                        <p className="mb-0">{point.hours || 'N/A'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {filteredPoints.length === 0 && (
                    <div className="text-center text-muted mt-4">
                        Aucun point relais trouvé dans cette zone
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default RelayPoints;